import { useState, useEffect, useCallback } from 'react';
import { CheckCircle, XCircle, Clock, Eye, Stamp, Trash } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { useAuth } from '../../contexts/AuthContext';
import { useCurrency } from '../../contexts/CompanyContext';
import { toast } from 'sonner';
import RowActionsMenu from '../../components/common/RowActionsMenu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ApprovalsPage() {
  const { token, user } = useAuth();
  const { formatCurrency } = useCurrency();
  const [activeTab, setActiveTab] = useState('pending');
  const [approvals, setApprovals] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [actionType, setActionType] = useState('approve');
  const [comments, setComments] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [allRes, pendingRes] = await Promise.all([
        fetch(`${API}/api/approvals`, { headers }),
        fetch(`${API}/api/approvals/pending`, { headers })
      ]);
      
      if (allRes.ok) setApprovals(await allRes.json());
      if (pendingRes.ok) setPendingApprovals(await pendingRes.json());
    } catch (err) {
      toast.error('Failed to load approvals');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAction = async (force = false) => {
    if (!selectedApproval) return;
    // Defensive: if called with an event (e.g. from onClick={handleAction}), coerce to boolean
    const forceBool = force === true;
    
    try {
      const res = await fetch(`${API}/api/approvals/${selectedApproval.id}/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          action: actionType,
          comments: comments || null,
          force: forceBool,
        })
      });
      
      if (res.ok) {
        const verb = actionType === 'approve' ? 'approved' : 'rejected';
        toast.success(`Request ${verb} successfully`);
        setShowActionModal(false);
        setSelectedApproval(null);
        setComments('');
        fetchData();
      } else {
        let err = {};
        let rawText = '';
        try {
          rawText = await res.clone().text();
          err = await res.json();
        } catch {
          err = { detail: rawText };
        }
        const d = err?.detail;
        if (res.status === 409 && d?.code === 'INSUFFICIENT_BALANCE') {
          const ok = window.confirm(
            `⚠️ Insufficient Balance\n\n` +
            `Account: ${d.account_name}\n` +
            `Current: ${d.current_balance.toFixed(2)}\n` +
            `Requested: ${d.requested_amount.toFixed(2)}\n` +
            `Projected: ${d.projected_balance.toFixed(2)} (NEGATIVE)\n\n` +
            `Continue anyway?`
          );
          if (ok) return handleAction(true);
        } else {
          // Try every shape: top-level message (new global handler), structured detail
          // {code,message}, FastAPI validation array, or raw string body.
          let msg = '';
          if (err?.message) {
            msg = err.code ? `[${err.code}] ${err.message}` : err.message;
          } else if (typeof d === 'string') {
            msg = d;
          } else if (d?.message) {
            msg = d.code ? `[${d.code}] ${d.message}` : d.message;
          } else if (Array.isArray(d) && d.length) {
            msg = d.map(e => `${(e.loc || []).join('.')}: ${e.msg}`).join(' • ');
          } else if (rawText) {
            msg = rawText.slice(0, 400);
          } else {
            msg = `${actionType === 'approve' ? 'Approval' : 'Rejection'} failed (HTTP ${res.status})`;
          }
          toast.error(msg, { duration: 8000 });
          console.error('Approval action failed:', { status: res.status, body: err, raw: rawText });
        }
      }
    } catch (err) {
      toast.error(`Network error: ${err?.message || 'Could not reach server'}`, { duration: 6000 });
    }
  };

  const openActionModal = (approval, action) => {
    setSelectedApproval(approval);
    setActionType(action);
    setComments('');
    setShowActionModal(true);
  };

  const handleDeleteApproval = async (approval) => {
    const linkedVoucher = approval.document_number || approval.document_type;
    const isPending = approval.status === 'pending';
    if (!window.confirm(
      `Delete this approval row?\n\n` +
      `${linkedVoucher} · ${approval.status}\n\n` +
      (isPending
        ? `Note: this is a PENDING approval. Deleting it will revert its linked voucher back to "draft" so you can edit and re-submit.\n\n`
        : `This will simply remove the approval record. No voucher data is touched.\n\n`) +
      `Continue?`
    )) return;
    try {
      const res = await fetch(`${API}/api/approvals/${approval.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Approval row deleted');
        fetchData();
      } else {
        let body = {};
        try { body = await res.json(); } catch { /* ignore */ }
        toast.error(body?.message || body?.detail || `Delete failed (HTTP ${res.status})`);
      }
    } catch (err) {
      toast.error(`Network error: ${err?.message || err}`);
    }
  };

  const [healing, setHealing] = useState(false);
  const runAccountingHealthCheck = async (autoFix = false) => {
    try {
      setHealing(true);
      const res = await fetch(
        `${API}/api/vouchers/accounting/health-check?auto_fix=${autoFix}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await res.json();
      if (!res.ok) {
        toast.error(data?.message || data?.detail?.message || 'Health check failed');
        return;
      }

      const fmtList = (label, items, mapFn, max = 8) => {
        if (!items?.length) return `• ${label}: 0\n`;
        const head = items.slice(0, max).map(mapFn).join('\n     ');
        const more = items.length > max ? `\n     …and ${items.length - max} more` : '';
        return `• ${label}: ${items.length}\n     ${head}${more}\n`;
      };

      const summary =
        `📊 Accounting Health Check\n` +
        `${'─'.repeat(45)}\n` +
        `Healthy: ${data.healthy ? 'YES ✅' : 'NO ⚠️'}\n` +
        `Total issues: ${data.total_issues}\n\n` +
        fmtList(
          '🔴 Vouchers with INCOMPLETE DATA (will fail approval)',
          data.issues.vouchers_with_incomplete_data,
          v => `${v.voucher_number} (${v.status}) · AED ${v.amount} · ${v.problems.join(', ')}`,
          10
        ) +
        fmtList(
          'Approved vouchers MISSING journal entry',
          data.issues.approved_vouchers_missing_je,
          v => `${v.voucher_number} · ${v.voucher_type} · AED ${v.amount}`
        ) +
        fmtList(
          'Unbalanced journal entries (DR ≠ CR)',
          data.issues.unbalanced_journal_entries,
          j => `${j.entry_number || j.journal_entry_id} · ${j.reference || ''} · diff ${j.difference}`
        ) +
        fmtList(
          '⚠️ JEs with ORPHAN account refs (silently dropped from reports)',
          data.issues.journal_entries_with_orphan_accounts,
          j => `JE#${j.entry_number} · ${j.reference || ''} · AED ${j.total_orphan_amount} on deleted account`
        ) +
        fmtList(
          'Pending vouchers stuck without approval row',
          data.issues.pending_vouchers_without_approval,
          v => `${v.voucher_number} · ${v.voucher_type} · AED ${v.amount}`
        ) +
        fmtList(
          'Orphan approval rows (voucher deleted)',
          data.issues.orphan_approvals,
          a => `${a.document_number || a.approval_id?.slice(0, 8)} · ${a.status}`
        ) +
        (data.issues.vouchers_with_incomplete_data?.length
          ? `\n${'─'.repeat(45)}\n💡 To fix incomplete-data vouchers: go to Vouchers page,\n   click the amber ✏️ Edit icon, fill missing fields, Save.\n`
          : '') +
        (autoFix
          ? `\n${'─'.repeat(45)}\nFixes applied:\n` +
            `  ✓ Journal entries created: ${data.fixes_applied.journal_entries_created}\n` +
            `  ✓ Approval rows recreated: ${data.fixes_applied.approvals_recreated}\n` +
            `  ✓ Orphan approvals cleaned: ${data.fixes_applied.orphan_approvals_cleaned || 0}\n`
          : (data.total_issues > 0
              ? `\n${'─'.repeat(45)}\n💡 Click "Auto-Heal" to fix the items above automatically.\n`
              : '')
        );
      window.alert(summary);
      if (autoFix) fetchData();
    } catch (err) {
      toast.error(`Health check error: ${err?.message || err}`);
    } finally {
      setHealing(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-500/10 text-amber-600',
      approved: 'bg-emerald-500/10 text-emerald-600',
      rejected: 'bg-red-500/10 text-red-600'
    };
    return <Badge className={styles[status] || styles.pending}>{status}</Badge>;
  };

  const getDocTypeBadge = (docType) => {
    const styles = {
      purchase_order: 'bg-purple-500/10 text-purple-600',
      payroll: 'bg-blue-500/10 text-blue-600',
      invoice: 'bg-emerald-500/10 text-emerald-600'
    };
    const labels = {
      purchase_order: 'Purchase Order',
      payroll: 'Payroll',
      invoice: 'Invoice'
    };
    return <Badge className={styles[docType] || 'bg-gray-500/10 text-gray-600'}>{labels[docType] || docType}</Badge>;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const statCards = [
    { label: 'Pending', value: pendingApprovals.length, icon: Clock, color: 'bg-amber-500/10 text-amber-600' },
    { label: 'Approved', value: approvals.filter(a => a.status === 'approved').length, icon: CheckCircle, color: 'bg-emerald-500/10 text-emerald-600' },
    { label: 'Rejected', value: approvals.filter(a => a.status === 'rejected').length, icon: XCircle, color: 'bg-red-500/10 text-red-600' },
    { label: 'Total', value: approvals.length, icon: Stamp, color: 'bg-blue-500/10 text-blue-600' },
  ];

  const canApprove = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="approvals-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const ApprovalTable = ({ items, showActions = false }) => (
    <div className="card-tactile rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Document</TableHead>
            <TableHead>Type</TableHead>
            <TableHead className="hidden sm:table-cell">Amount</TableHead>
            <TableHead className="hidden md:table-cell">Requested By</TableHead>
            <TableHead className="hidden lg:table-cell">Date</TableHead>
            <TableHead>Status</TableHead>
            {showActions && <TableHead className="text-right">Actions</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={showActions ? 7 : 6} className="text-center py-8 text-muted-foreground">
                No approval requests found
              </TableCell>
            </TableRow>
          ) : (
            items.map((approval) => (
              <TableRow key={approval.id} data-testid={`approval-row-${approval.id}`}>
                <TableCell className="font-medium">{approval.document_number || approval.document_id?.substring(0, 8)}</TableCell>
                <TableCell>{getDocTypeBadge(approval.document_type)}</TableCell>
                <TableCell className="hidden sm:table-cell">{approval.amount ? formatCurrency(approval.amount) : '-'}</TableCell>
                <TableCell className="hidden md:table-cell">{approval.requested_by_name || '-'}</TableCell>
                <TableCell className="hidden lg:table-cell">{formatDate(approval.created_at)}</TableCell>
                <TableCell>{getStatusBadge(approval.status)}</TableCell>
                {showActions && (
                  <TableCell className="text-right">
                    <div className="flex justify-end">
                      <RowActionsMenu
                        testid={`approval-${approval.id}`}
                        actions={[
                          approval.status === 'pending' && { id: 'approve', label: 'Approve', icon: CheckCircle, onClick: () => openActionModal(approval, 'approve') },
                          approval.status === 'pending' && { id: 'reject', label: 'Reject', icon: XCircle, onClick: () => openActionModal(approval, 'reject'), destructive: true },
                          approval.status === 'pending' && 'separator',
                          { id: 'delete', label: 'Delete row (admin)', icon: Trash, onClick: () => handleDeleteApproval(approval), destructive: true },
                        ].filter(Boolean)}
                      />
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <div className="space-y-8" data-testid="approvals-page">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-semibold">
            Approvals
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mt-1">
            Review and approve pending requests
          </p>
        </div>
        {canApprove && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => runAccountingHealthCheck(false)}
              disabled={healing}
              data-testid="accounting-health-check-btn"
              title="Scan accounting for inconsistencies (read-only)"
            >
              {healing ? 'Checking…' : 'Health Check'}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                if (window.confirm('Auto-heal will back-fill missing journal entries and re-create broken approval records. Continue?')) {
                  runAccountingHealthCheck(true);
                }
              }}
              disabled={healing}
              className="text-amber-600 border-amber-200 hover:bg-amber-50"
              data-testid="accounting-auto-heal-btn"
              title="Automatically repair missing JEs and orphan approvals"
            >
              Auto-Heal
            </Button>
          </div>
        )}
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="card-tactile rounded-lg p-4 sm:p-6"
          >
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-lg ${stat.color}`}>
                <stat.icon size={20} weight="duotone" />
              </div>
              <div>
                <span className="text-xs tracking-wider uppercase font-semibold text-muted-foreground">
                  {stat.label}
                </span>
                <p className="text-xl sm:text-2xl font-heading font-semibold mt-0.5">
                  {stat.value}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid grid-cols-2 w-full max-w-sm">
          <TabsTrigger value="pending" data-testid="tab-pending">
            Pending {pendingApprovals.length > 0 && `(${pendingApprovals.length})`}
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          {canApprove ? (
            <ApprovalTable items={pendingApprovals} showActions={true} />
          ) : (
            <div className="card-tactile rounded-lg p-8 text-center text-muted-foreground">
              <Stamp size={48} weight="duotone" className="mx-auto mb-4 opacity-50" />
              <p>You don't have permission to approve requests.</p>
              <p className="text-sm mt-2">Contact your administrator for approval access.</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="history">
          <ApprovalTable items={approvals.filter(a => a.status !== 'pending')} showActions={false} />
        </TabsContent>
      </Tabs>

      {/* Action Modal */}
      <Dialog open={showActionModal} onOpenChange={setShowActionModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'approve' ? 'Approve Request' : 'Reject Request'}
            </DialogTitle>
            <DialogDescription>
              {selectedApproval ? (
                <>
                  {selectedApproval.document_number || 'Document'}
                  {selectedApproval.amount ? ` · ${formatCurrency(selectedApproval.amount)}` : ''}
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          {selectedApproval && (
            <div className="flex items-center gap-2 -mt-2">
              {getDocTypeBadge(selectedApproval.document_type)}
            </div>
          )}
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Comments (optional)</Label>
              <Textarea 
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder={actionType === 'approve' ? 'Add approval notes...' : 'Reason for rejection...'}
                rows={3}
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowActionModal(false)}>Cancel</Button>
            <Button 
              onClick={() => handleAction(false)}
              className={actionType === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}
              data-testid="confirm-action-btn"
            >
              {actionType === 'approve' ? (
                <><CheckCircle size={16} className="mr-2" /> Approve</>
              ) : (
                <><XCircle size={16} className="mr-2" /> Reject</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
